import { z } from 'zod'

import type {
  CommonResponseCollectionStudentGroupResponse,
  CommonResponseCollectionStudentResponse,
  CommonResponsePageStudentResponse,
  CommonResponseSetStudentAppSettingResponse,
  CommonResponseString,
  CommonResponseStudentAppSettingResponse,
  CommonResponseStudentDetailResponse,
  CommonResponseStudentExcelSuccessResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { type Page, pageSchema } from '~/schema/page'

import {
  type Student,
  type StudentAppSetting,
  type StudentExcelFailureRow,
  StudentExcelFailureRowSchema,
  type StudentExcelResult,
  StudentExcelResultSchema,
  type StudentPhoneGroup,
  StudentPhoneGroupSchema,
  StudentSchema,
  type StudentSummary,
  StudentSummarySchema,
} from './schemas'

export const adaptStudent = (raw: CommonResponseStudentDetailResponse['data']): Student =>
  parseApiResponse(StudentSchema, raw, 'Student')

// 학원 안에서 아직 쓰지 않은 출결번호. 서버가 4자리로 0 을 채운 문자열로 준다.
export const adaptUsableAttendanceCode = (
  raw: CommonResponseString['data'],
): Student['attendanceCode'] => parseApiResponse(z.string(), raw, 'Student')

// 생성 타입 StudentAppSettingResponse 는 3필드(studentId·lectureService·availableDailyWorksheet)인데
// 실제 응답에는 show*/useAiFor* 플래그 8개가 더 온다. 2026-09-16 기준 근거는 팀이 녹음한 e2e 목
// (apps/mathflat-student/__tests__/e2e/_mocks/data/auth/student-app-setting.ts)과 teacher 의 ambient
// res.StudentAppSetting 이 같은 11필드를 적고 있다는 것이다. 라이브 응답으로 직접 확인한 것은 아니다.
// teacher 가 쓰는 목록 엔드포인트(/students/app-setting)도 같은 응답 클래스를 내므로 결함은 두 경로에 걸치고, 둘 다 이 스키마를 거친다.
// 아래 스키마가 실제 와이어 모양을 검증하므로 입력은 생성 타입으로 받되 asWire 한 줄에서만 unknown 을 거친다.
// 백엔드가 선언 타입을 맞추면 이 스키마를 생성 타입으로 되돌리고 캐스트와 주석을 지운다.
export const StudentAppSettingWireSchema = z.object({
  studentId: z.string(),
  showSolutionBeforeScoring: z.boolean(),
  showSolutionAfterScoring: z.boolean(),
  showProblemVideoBeforeScoring: z.boolean(),
  showProblemVideoAfterScoring: z.boolean(),
  showSolutionToParent: z.boolean(),
  lectureService: z.boolean(),
  useAiForHintConceptual: z.boolean(),
  useAiForSolveTheStuckUp: z.boolean(),
  useAiForReviewTheSolution: z.boolean(),
  availableDailyWorksheet: z.boolean(),
})
export type StudentAppSettingWire = z.infer<typeof StudentAppSettingWireSchema>
const asWire = (
  raw:
    | CommonResponseStudentAppSettingResponse['data']
    | CommonResponseSetStudentAppSettingResponse['data'],
): unknown => raw

// 이름 변환은 검증 뒤에 둔다. lenient 에서 검증이 실패해도 앱은 한글 이름으로 값을 받아야 한다.
// lenient 는 본문이 없을 때 undefined 를 그대로 통과시키므로 변환 전에 그 경우를 건넌다.
const toStudentAppSetting = (w: StudentAppSettingWire): StudentAppSetting => ({
  studentId: w.studentId,
  일반채점_채점전정답공개: w.showSolutionBeforeScoring,
  채점후정답해설공개: w.showSolutionAfterScoring,
  채점전문풀동공개: w.showProblemVideoBeforeScoring,
  채점후문풀동공개: w.showProblemVideoAfterScoring,
  학부모정답해설공개: w.showSolutionToParent,
  lectureService: w.lectureService,
  개념힌트_사용: w.useAiForHintConceptual,
  풀이분석_사용: w.useAiForSolveTheStuckUp,
  풀이확인_사용: w.useAiForReviewTheSolution,
  availableDailyWorksheet: w.availableDailyWorksheet,
})

export const adaptStudentAppSetting = (
  raw: CommonResponseStudentAppSettingResponse['data'],
): StudentAppSetting => {
  const w = parseApiResponse(StudentAppSettingWireSchema, asWire(raw), 'StudentAppSetting')
  return w && toStudentAppSetting(w)
}

export const adaptStudentAppSettings = (
  raw: CommonResponseSetStudentAppSettingResponse['data'],
): StudentAppSetting[] =>
  parseApiResponse(z.array(StudentAppSettingWireSchema), asWire(raw), 'StudentAppSetting')?.map(
    toStudentAppSetting,
  )

// 목록 응답의 생성 타입 StudentResponse 는 이름이 같은 다른 서버 클래스(학습지 예약 대상 학생)로 그려져 있다.
// springdoc 이 같은 이름의 클래스를 하나로 합친 결과라 실제 와이어와 필드가 전혀 다르다. 그래서 목록 둘은
// 이 한 자리에서 unknown 으로 넘기고 스키마가 실제 모양을 검증한다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 student/controller/response/StudentResponse.kt 와 팀이 녹음한
// teacher e2e 목(_mocks/data/common/students.ts)으로 확인.
const asSummaryWire = (
  raw: CommonResponsePageStudentResponse['data'] | CommonResponseCollectionStudentResponse['data'],
): unknown => raw

export const adaptStudentSummaryPage = (
  raw: CommonResponsePageStudentResponse['data'],
): Page<StudentSummary> =>
  parseApiResponse(pageSchema(StudentSummarySchema), asSummaryWire(raw), 'Student')

export const adaptStudentSummaries = (
  raw: CommonResponseCollectionStudentResponse['data'],
): StudentSummary[] =>
  parseApiResponse(z.array(StudentSummarySchema), asSummaryWire(raw), 'Student')

// 생성 타입은 그룹 멤버를 공통 베이스(id·name)로만 그린다. 실제로는 하위 클래스
// StudentMemberWithPhoneNumberResponse 가 직렬화되어 전화번호가 함께 온다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 student/controller/response/StudentMemberResponse.kt 로 확인.
const asGroupWire = (raw: CommonResponseCollectionStudentGroupResponse['data']): unknown => raw

export const adaptStudentPhoneGroups = (
  raw: CommonResponseCollectionStudentGroupResponse['data'],
): StudentPhoneGroup[] =>
  parseApiResponse(z.array(StudentPhoneGroupSchema), asGroupWire(raw), 'Student')

// 실패 행은 오류 본문에 실려 오므로 응답 경계 검증 대신 모양만 가려낸다. 엑셀 실패가 아닌 오류면 null 이다.
export const parseStudentExcelFailureRows = (
  errorData: unknown,
): StudentExcelFailureRow[] | null => {
  const result = z.array(StudentExcelFailureRowSchema).safeParse(errorData)
  return result.success ? result.data : null
}

export const adaptStudentExcelResult = (
  raw: CommonResponseStudentExcelSuccessResponse['data'],
): StudentExcelResult => parseApiResponse(StudentExcelResultSchema, raw, 'Student')
